import { useState } from "react";
import { supabase } from "../lib/supabase";
import posthog from "../lib/posthog";
import bouttimeLogo from "../assets/bouttime-logo.svg";

const CREAM = "linear-gradient(180deg,#FAF3E8 0%,#F2E8D5 100%)";
const GREEN = "#1B4332";
const TEXT = "#1A1A1A";
const MUTED = "#5A5A5A";
const ERROR_COLOR = "#E6544C"; // appTheme.actionColor
const HEADING_FONT = "'Fraunces', Georgia, serif";
const BODY_FONT    = "'Satoshi', sans-serif";

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "14px 16px",
  borderRadius: "14px",
  border: "1px solid rgba(27,67,50,0.22)",
  background: "rgba(255,255,255,0.95)",
  fontSize: "15px",
  color: TEXT,
  outline: "none",
  fontFamily: BODY_FONT,
};

function EyeIcon({ open }) {
  return open ? (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke={MUTED} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke={MUTED} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}


// ── Sign In ───────────────────────────────────────────────────────────────────
function SignIn({ onSwitch, onGoToOnboarding }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resetSent, setResetSent] = useState(false);

  async function handleSignIn(e) {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }
    setLoading(true);
    const { error: err } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (err) {
      setError(
        err.message.toLowerCase().includes("invalid login credentials")
          ? "Incorrect email or password — please try again."
          : err.message
      );
      return;
    }
    posthog.capture("user_signed_in");
  }

  async function handleForgotPassword() {
    if (!email) { setError("Enter your email address first, then tap Forgot password."); return; }
    setError("");
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin });
    if (err) setError(err.message);
    else setResetSent(true);
  }

  return (
    <form onSubmit={handleSignIn} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <div style={{ textAlign: "center", marginBottom: "8px" }}>
        <h1 style={{ margin: "0 0 6px", fontSize: "26px", fontWeight: "500", fontFamily: HEADING_FONT, color: TEXT }}>Welcome back</h1>
        <p style={{ margin: 0, fontSize: "14px", color: MUTED }}>Sign in to your account</p>
      </div>


      <div style={{ position: "relative" }}>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email address" style={inputStyle} />
      </div>
      <div style={{ position: "relative" }}>
        <input type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" style={{ ...inputStyle, paddingRight: "46px" }} />
        <button type="button" onClick={() => setShowPw(p => !p)} style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }}>
          <EyeIcon open={showPw} />
        </button>
      </div>

      <button type="button" onClick={handleForgotPassword} style={{ background: "none", border: "none", cursor: "pointer", color: GREEN, fontSize: "13px", fontWeight: "600", textAlign: "right", padding: "0", fontFamily: BODY_FONT }}>
        Forgot password?
      </button>

      {resetSent && <p style={{ margin: 0, fontSize: "13px", color: GREEN, textAlign: "center" }}>✓ Password reset email sent — check your inbox.</p>}
      {error && <p style={{ margin: 0, fontSize: "13px", color: ERROR_COLOR, fontFamily: BODY_FONT, textAlign: "center" }}>{error}</p>}

      <button type="submit" disabled={loading} style={{ width: "100%", padding: "15px", borderRadius: "14px", border: "none", background: GREEN, color: "#fff", fontWeight: "700", fontSize: "15px", cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1, fontFamily: BODY_FONT }}>
        {loading ? "Signing in…" : "Sign in"}
      </button>

      <p style={{ margin: 0, textAlign: "center", fontSize: "14px", color: MUTED }}>
        Don't have an account?{" "}
        <button type="button" onClick={onGoToOnboarding || onSwitch} style={{ background: "none", border: "none", cursor: "pointer", color: GREEN, fontWeight: "700", fontSize: "14px", padding: 0, fontFamily: BODY_FONT }}>Get started</button>
      </p>
    </form>
  );
}

// ── Sign Up ───────────────────────────────────────────────────────────────────
function SignUp({ onSwitch }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [emailExists, setEmailExists] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSignUp(e) {
    e.preventDefault();
    setError("");
    setEmailExists(false);
    setLoading(true);
    const { data, error: err } = await supabase.auth.signUp({ email, password });
    setLoading(false);
    if (err) {
      if (err.message.toLowerCase().includes("already")) {
        setEmailExists(true);
      } else {
        setError(err.message);
      }
      return;
    }
    // Supabase v2 with email confirmation: existing user returns empty identities array
    if (data?.user?.identities?.length === 0) {
      setEmailExists(true);
      return;
    }
    // Create profile row
    if (data?.user) {
      await supabase.from("user_profiles").insert({ id: data.user.id, email, name: "" });
    }
    posthog.capture("user_signed_up");
    setDone(true);
  }

  if (done) {
    return (
      <div style={{ textAlign: "center", padding: "20px 0" }}>
        <div style={{ fontSize: "48px", marginBottom: "16px" }}>✉️</div>
        <h2 style={{ margin: "0 0 10px", fontSize: "22px", fontWeight: "700", fontFamily: HEADING_FONT, color: TEXT }}>Check your email</h2>
        <p style={{ margin: "0 0 20px", fontSize: "14px", color: MUTED, lineHeight: 1.6 }}>We sent a confirmation link to <strong>{email}</strong>. Click it to activate your account.</p>
        <button onClick={onSwitch} style={{ background: "none", border: "none", cursor: "pointer", color: GREEN, fontWeight: "700", fontSize: "14px", fontFamily: BODY_FONT }}>Back to sign in</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSignUp} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <div style={{ textAlign: "center", marginBottom: "8px" }}>
        <h1 style={{ margin: "0 0 6px", fontSize: "26px", fontWeight: "500", fontFamily: HEADING_FONT, color: TEXT }}>Create your account</h1>
        <p style={{ margin: 0, fontSize: "14px", color: MUTED }}>Start planning meals that work for you</p>
      </div>


      <div>
        <input type="email" value={email} onChange={e => { setEmail(e.target.value); setEmailExists(false); }} placeholder="Email address" required style={inputStyle} />
        {emailExists && (
          <div style={{ marginTop: "8px" }}>
            <p style={{ margin: 0, fontSize: "13px", color: "#dc2626" }}>An account with this email already exists.</p>
            <button type="button" onClick={onSwitch} style={{ background: "none", border: "none", cursor: "pointer", color: GREEN, fontWeight: "700", fontSize: "13px", padding: "4px 0 0", fontFamily: BODY_FONT }}>
              Sign in instead →
            </button>
          </div>
        )}
      </div>
      <div style={{ position: "relative" }}>
        <input type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="Password (min 6 characters)" required minLength={6} style={{ ...inputStyle, paddingRight: "46px" }} />
        <button type="button" onClick={() => setShowPw(p => !p)} style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }}>
          <EyeIcon open={showPw} />
        </button>
      </div>

      {!emailExists && error && <p style={{ margin: 0, fontSize: "13px", color: "#dc2626", textAlign: "center" }}>{error}</p>}

      <button type="submit" disabled={loading} style={{ width: "100%", padding: "15px", borderRadius: "14px", border: "none", background: GREEN, color: "#fff", fontWeight: "700", fontSize: "15px", cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1, fontFamily: BODY_FONT }}>
        {loading ? "Creating account…" : "Create account"}
      </button>

      <p style={{ margin: 0, textAlign: "center", fontSize: "14px", color: MUTED }}>
        Already have an account?{" "}
        <button type="button" onClick={onSwitch} style={{ background: "none", border: "none", cursor: "pointer", color: GREEN, fontWeight: "700", fontSize: "14px", padding: 0, fontFamily: BODY_FONT }}>Sign in</button>
      </p>
    </form>
  );
}

// ── Root auth screen ──────────────────────────────────────────────────────────
function AuthScreen({ onGoToOnboarding }) {
  const [view, setView] = useState("signin");

  return (
    <div style={{ minHeight: "100vh", background: CREAM, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 20px", fontFamily: BODY_FONT }}>
      <div style={{ width: "100%", maxWidth: "400px" }}>
        {/* Logo */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "32px" }}>
          <img src={bouttimeLogo} alt="BoutTime" style={{ height: "38px", width: "auto" }} />
        </div>

        {/* Card */}
        <div style={{ background: "rgba(255,255,255,0.92)", borderRadius: "24px", padding: "28px 24px", boxShadow: "0 4px 32px rgba(27,67,50,0.10)", border: "1px solid rgba(27,67,50,0.10)" }}>
          {view === "signin"
            ? <SignIn onSwitch={() => setView("signup")} onGoToOnboarding={onGoToOnboarding} />
            : <SignUp onSwitch={() => setView("signin")} />
          }
        </div>
      </div>
    </div>
  );
}

export default AuthScreen;
